import supabase from "@/services/supabaseClient.js";

function cleanText(value) {
  return String(value || "").trim();
}

function readEnvironment() {
  const explicit = cleanText(import.meta.env.VITE_APP_ENV).toLowerCase();
  if (explicit) return explicit;
  try {
    const host = cleanText(window.location.hostname).toLowerCase();
    if (host === "staging.afrimed.tech" || host.includes("staging")) return "staging";
    if (host === "localhost" || host === "127.0.0.1") return "local";
  } catch {
    // ignore
  }
  return "staging";
}

async function readRecentActivityEventId(userId) {
  const uid = cleanText(userId);
  if (!uid) return null;

  try {
    const { data, error } = await supabase
      .from("user_activity_events")
      .select("id")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return null;
    return data?.id || null;
  } catch {
    return null;
  }
}

export function resolveFeedbackProfileRole(auth = {}) {
  return cleanText(
    auth?.profile?.role ||
      auth?.role ||
      auth?.user?.user_metadata?.primary_role ||
      auth?.user?.user_metadata?.role ||
      auth?.user?.app_metadata?.role ||
      "",
  ).toLowerCase();
}

export async function submitFeedbackReport({
  auth = {},
  feedbackType = "",
  roleContext = "",
  area = "",
  severity = "Medium",
  message = "",
  contactOk = false,
  routeContext = "",
  referrer = "",
} = {}) {
  const userId = cleanText(auth?.user?.id);
  const email = cleanText(auth?.user?.email || auth?.profile?.email).toLowerCase();
  const cleanMessage = cleanText(message);

  if (!cleanMessage) {
    throw new Error("Please add a short note before sending feedback.");
  }

  const recentActivityEventId = await readRecentActivityEventId(userId);
  const userAgent =
    typeof navigator !== "undefined" ? cleanText(navigator.userAgent) : "";

  const { data, error } = await supabase
    .from("feedback_reports")
    .insert({
      user_id: userId || null,
      email: email || null,
      feedback_type: cleanText(feedbackType) || "General suggestion",
      role_context: cleanText(roleContext) || null,
      area: cleanText(area) || null,
      severity: cleanText(severity).toLowerCase() || "medium",
      message: cleanMessage,
      contact_ok: Boolean(contactOk),
      route_context: cleanText(routeContext) || null,
      referrer: cleanText(referrer) || null,
      user_agent: userAgent || null,
      environment: readEnvironment(),
      recent_activity_event_id: recentActivityEventId,
      session_id: null,
      callback_attempt_id: null,
    })
    .select("id")
    .single();

  if (error) throw error;
  return data;
}

export async function sendFeedbackMagicLink(email) {
  const cleanEmail = cleanText(email).toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
    throw new Error("Enter a valid email address.");
  }

  const origin =
    typeof window !== "undefined"
      ? String(window.location.origin || "").replace(/\/+$/, "")
      : "https://staging.afrimed.tech";
  const redirectTo = `${origin}/auth/callback?next=/feedback`;
  const sentAt = Date.now();

  try {
    localStorage.setItem(
      "afrimed:pending_magic_context",
      JSON.stringify({
        email: cleanEmail,
        sentAt,
        next: "/feedback",
        member: "",
        invite: "feedback",
        attemptId: "",
      }),
    );
    localStorage.setItem("afrimed:pending_magic_email", cleanEmail);
    localStorage.setItem("afrimed:pending_magic_sent_at", String(sentAt));
  } catch {
    // ignore storage failures
  }

  const { error } = await supabase.auth.signInWithOtp({
    email: cleanEmail,
    options: {
      shouldCreateUser: false,
      emailRedirectTo: redirectTo,
    },
  });

  if (error) throw error;
  return { ok: true, email: cleanEmail };
}
